import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Heart, GitCompare, MessageSquareText, Calendar, Award, Tag, DollarSign,
  ShoppingBag, ChevronLeft, ChevronRight, BadgeCheck, Fuel, Gauge, Cog,
  ChevronDown, ChevronUp, Sparkles, Info,
} from "lucide-react";
import Seo, { breadcrumbs, vehicleSchema } from "../components/Seo";
import LeadForm from "../components/LeadForm";
import VehicleCard from "../components/VehicleCard";
import { SectionHeader, Disclaimer } from "../components/UI";
import { getVehicleBySlug, getSimilar, api } from "../lib/api";
import { siteConfig, fmtMoney, fmtMiles, disclaimers, fees } from "../config";
import { useFees } from "../lib/feeSettings";
import { savedStore, compareStore } from "../lib/storage";
import { buildPriceStack, privateOffersForVehicle } from "../lib/pricing";

const COMPARE_LIMIT_MSG = "You can compare up to 6 vehicles. Remove one to add another.";

const QUICK = [
  "Is it still available?",
  "What would payments look like?",
  "Can I trade my vehicle?",
  "Can I come see it today?",
  "Do you have similar options?",
  "Can I preview a deal in Build My Own Deal on this?",
];

/* ---------------- Feature grouping ---------------- */
const FEATURE_BUCKETS = [
  ["Packages", /package|equipment group|pkg\b|edition|preferred equipment/i],
  ["Safety & Driver Assistance", /safety|airbag|air bag|abs|brake assist|lane|blind|collision|traction|stability|rear vision|camera|park assist|parking sensor|alert|monitoring|cross traffic|cruise|stabilitrak|tire pressure|security|alarm|onstar/i],
  ["Technology & Entertainment", /audio|bluetooth|carplay|android|wi-?fi|usb|navigation|display|touch.?screen|speaker|radio|sirius|infotainment|mylink|wireless|charging pad|voice|app|connected/i],
  ["Performance & Mechanical", /engine|transmission|axle|suspension|drivetrain|4wd|awd|locking|differential|tow|trailer|exhaust|turbo|alternator|battery|cooling|shift|drive mode|hp\b|v6|v8|four wheel|all.?wheel/i],
  ["Exterior", /wheel|tire|roof rail|spoiler|fog|headlamp|headlight|lamp|grille|bumper|running board|bed liner|chrome|tinted|mirror|liftgate|tailgate|moonroof|sunroof|luggage|window/i],
  ["Comfort & Convenience", /seat|climate|air conditioning|keyless|remote|power|steering wheel|console|armrest|sunshade|cargo|visor|cup|mat|entry|start|defogger|wiper/i],
  ["Interior", /interior|leather|cloth|vinyl|carpet|trim|lighting|headliner|upholstery/i],
];

function groupFeatures(options = []) {
  const groups = {};
  const order = [...FEATURE_BUCKETS.map(([n]) => n), "Other"];
  options.forEach((opt) => {
    const bucket = FEATURE_BUCKETS.find(([, re]) => re.test(opt));
    const name = bucket ? bucket[0] : "Other";
    (groups[name] = groups[name] || []).push(opt);
  });
  return order.filter((n) => groups[n]?.length).map((n) => [n, groups[n]]);
}

/* ---------------- Sub-components ---------------- */

function Gallery({ photos, title, isNew }) {
  const [idx, setIdx] = useState(0);
  const total = photos.length;
  const go = (d) => setIdx((i) => (i + d + total) % total);

  return (
    <div data-testid="vd-gallery" className="min-w-0">
      <div className="relative rounded-2xl overflow-hidden bg-[var(--ssc-silver-2)]" style={{ aspectRatio: "16/10" }}>
        {photos[idx] ? (
          <img src={photos[idx].url} alt={photos[idx].alt || `${title} photo ${idx + 1}`} loading="eager" className="w-full h-full object-cover" data-testid="vd-main-photo" />
        ) : (
          <div className="w-full h-full grid place-items-center text-[var(--ssc-ink-2)] text-sm p-4 text-center">
            Photo coming soon. Text Scott for details.
          </div>
        )}
        <span className={`absolute top-3 left-3 ssc-badge ${isNew ? "ssc-badge-new" : "ssc-badge-used"}`}>{isNew ? "New" : "Used"}</span>
        {total > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Previous photo" data-testid="vd-photo-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/45 hover:bg-black/65 text-white grid place-items-center transition-colors">
              <ChevronLeft size={22} />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Next photo" data-testid="vd-photo-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/45 hover:bg-black/65 text-white grid place-items-center transition-colors">
              <ChevronRight size={22} />
            </button>
            <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs font-bold tabular-nums" data-testid="vd-photo-counter">
              {idx + 1} / {total}
            </span>
          </>
        )}
      </div>
      {total > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1 snap-x" data-testid="vd-thumbnails">
          {photos.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Photo ${i + 1}`}
              className={`shrink-0 w-24 h-[68px] md:w-28 md:h-20 rounded-lg overflow-hidden border-2 snap-start transition-all ${i === idx ? "border-[var(--ssc-blue)] ring-2 ring-[var(--ssc-blue)]/30" : "border-transparent opacity-80 hover:opacity-100"}`}
              data-testid={`vd-thumb-${i}`}
            >
              <img src={p.url} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PriceBreakdown({ v }) {
  const effFees = useFees();
  const docFee = effFees.doc_fee ?? fees.docFee;
  const titleFee = effFees.title_fee ?? fees.titleFee;
  const isNew = v.status === "new";

  const stack = buildPriceStack(v);
  const finalPrice = stack.vautoPrice + docFee + titleFee;
  const showMsrp = stack.mode !== "simple" && stack.msrp;

  const Row = ({ label, val, accent, strike, sub, testId }) => (
    <div className="flex items-baseline justify-between gap-3 py-1.5" data-testid={testId}>
      <span className={`text-sm ${accent ? "font-bold text-[var(--ssc-navy)]" : sub ? "text-emerald-700" : "text-[var(--ssc-ink-2)]"}`}>{label}</span>
      <span className={`tabular-nums font-bold ${strike ? "line-through text-[var(--ssc-ink-2)] font-semibold" : accent ? "text-[var(--ssc-navy)]" : sub ? "text-emerald-700" : "text-[var(--ssc-ink)]"}`}>{val}</span>
    </div>
  );

  return (
    <div className="rounded-2xl border border-[var(--ssc-line)] bg-white shadow-sm p-5" data-testid="vd-price-card">
      <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Pricing</div>
      <div className="mt-1 divide-y divide-[var(--ssc-line)]/60">
        <div>
          {showMsrp && <Row label="MSRP" val={fmtMoney(stack.msrp)} strike testId="vd-price-msrp" />}
          {stack.dealerDiscount > 0 && (
            <Row label="Dealer Discount" val={`−${fmtMoney(stack.dealerDiscount)}`} testId="vd-price-discount" />
          )}
          {stack.publicInlineOffers.map((o, i) => (
            <Row
              key={`pub-${i}`}
              label={o.name}
              val={`−${fmtMoney(o.amount)}`}
              sub
              testId={`vd-price-public-offer-${i}`}
            />
          ))}
          <Row label={isNew ? "Scott's Price" : "Advertised Price"} val={fmtMoney(stack.vautoPrice)} accent testId="vd-price-advertised" />
        </div>
        <div className="pt-1.5">
          <Row label={effFees.fee_label_doc || "Documentation Fee"} val={`+${fmtMoney(docFee)}`} testId="vd-price-doc-fee" />
          <Row label={effFees.fee_label_title || "Title Fee"} val={`+${fmtMoney(titleFee)}`} testId="vd-price-title-fee" />
        </div>
      </div>
      <div className="mt-3 rounded-xl bg-[var(--ssc-navy)] text-white px-4 py-3 flex items-baseline justify-between gap-3" data-testid="vd-final-price">
        <span className="text-sm font-extrabold">Final Price Before Taxes</span>
        <span className="text-2xl font-black tabular-nums">{fmtMoney(finalPrice)}</span>
      </div>

      {stack.additionalPublicOffers.length > 0 && (
        <div
          className="mt-3 rounded-xl border-2 border-[var(--ssc-blue)]/30 bg-blue-50 px-4 py-3"
          data-testid="vd-additional-public-savings"
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs font-extrabold uppercase tracking-wide text-[var(--ssc-blue)]"
                  data-testid="vd-public-rebates-heading">
              Available Public Rebates
            </span>
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-[var(--ssc-ink)]">
            These offers may reduce your price if you qualify and choose a compatible deal structure.
            They are <strong>not automatically included in Scott&rsquo;s Price</strong>.
          </p>
          <ul className="mt-2 space-y-2">
            {stack.additionalPublicOffers.map((o, i) => (
              <li key={`add-${i}`} className="text-xs" data-testid={`vd-public-rebate-${i}`}>
                <div className="flex justify-between gap-3">
                  <span className="font-bold text-[var(--ssc-navy)] truncate pr-2">{o.name}</span>
                  <span className="tabular-nums font-extrabold text-[var(--ssc-navy)]">— {fmtMoney(o.amount)}</span>
                </div>
                <div className="text-[10px] text-[var(--ssc-ink-2)] italic">
                  Available public rebate. Not included in Scott&rsquo;s Price unless selected in Scott&rsquo;s Deal Desk and confirmed compatible.
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-[11px] leading-relaxed text-[var(--ssc-ink-2)] flex gap-1.5" data-testid="vd-price-note">
        <Info size={13} className="shrink-0 mt-0.5" />
        <span>
          Taxes and variable government/registration fees are additional unless
          specifically listed above. Final figures are confirmed with the dealership.
        </span>
      </p>
      {isNew && (
        <p className="mt-2 text-[10px] leading-relaxed text-[var(--ssc-ink-2)]/80" data-testid="vd-msrp-disclosure">
          MSRP, pricing, rebates, and availability may change. Offers vary by VIN,
          residency, eligibility, credit approval, lender, and program rules. See
          Scott for exact current pricing and incentives.
        </p>
      )}
    </div>
  );
}

function ByersPdfOffersSection({ v }) {
  const offers = privateOffersForVehicle(v);
  const financing = Array.isArray(v.byers_pdf_special_financing) ? v.byers_pdf_special_financing : [];
  if (offers.length === 0 && financing.length === 0) return null;

  return (
    <div className="rounded-2xl border border-[var(--ssc-line)] bg-white shadow-sm p-5 mt-4" data-testid="vd-byers-offers">
      {offers.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[var(--ssc-blue)]" />
            <h3 className="font-extrabold text-[var(--ssc-navy)]">Other Offers You May Qualify For</h3>
          </div>
          <ul className="mt-3 space-y-2.5">
            {offers.map((o, i) => (
              <li
                key={`${o.name}-${i}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1"
                data-testid={`vd-byers-offer-${i}`}
              >
                <span className="font-bold text-[var(--ssc-ink)] text-sm">{o.name}</span>
                {o.amount ? (
                  <span className="font-black text-[var(--ssc-navy)] tabular-nums text-sm">{fmtMoney(o.amount)}</span>
                ) : null}
                <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                  May require qualification
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {financing.length > 0 && (
        <div className={offers.length > 0 ? "mt-5 pt-4 border-t border-[var(--ssc-line)]/60" : ""}>
          <div className="flex items-center gap-2">
            <DollarSign size={16} className="text-[var(--ssc-blue)]" />
            <h3 className="font-extrabold text-[var(--ssc-navy)]">Special Financing Available</h3>
          </div>
          <ul className="mt-3 space-y-2.5">
            {financing.map((f, i) => (
              <li key={`fin-${i}`} className="text-sm text-[var(--ssc-ink)]" data-testid={`vd-byers-financing-${i}`}>
                {f.apr_pct != null && (
                  <span className="font-extrabold text-[var(--ssc-navy)]">
                    {f.apr_pct}% APR for {f.term_months} months
                  </span>
                )}
                {f.kind === "deferral" && f.deferral_days && (
                  <span className="font-extrabold text-[var(--ssc-navy)]">
                    {f.deferral_days}-day payment deferral
                  </span>
                )}
                {f.apr_pct != null && f.deferral_days ? (
                  <span className="text-[var(--ssc-ink-2)]">
                    {" "}+ {f.deferral_days}-day payment deferral
                  </span>
                ) : null}
                <span className="text-[var(--ssc-ink-2)]"> · for well-qualified buyers, when financed w/ GM Financial</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 text-[11px] text-[var(--ssc-ink-2)]" data-testid="vd-byers-offers-note">
        Conditional offers and special financing are shown for information only
        and are not subtracted from the price above. Eligibility, approval, and
        availability vary by program. Scott can confirm exactly what you qualify
        for.
      </p>
    </div>
  );
}

function OffersSection({ vin }) {
  const [offers, setOffers] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/rebates/for-vehicle/${vin}`)
      .then(({ data }) => on && setOffers(data.offers || []))
      .catch(() => on && setOffers([]));
    return () => { on = false; };
  }, [vin]);

  if (!offers || offers.length === 0) return null;

  const LABEL_STYLE = {
    "Included in advertised price": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "May require qualification": "bg-amber-50 text-amber-700 border-amber-200",
    "Manually applied": "bg-blue-50 text-blue-700 border-blue-200",
    "Informational only": "bg-slate-50 text-slate-600 border-slate-200",
  };

  return (
    <div className="rounded-2xl border border-[var(--ssc-line)] bg-white shadow-sm p-5 mt-4" data-testid="vd-offers">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[var(--ssc-blue)]" />
        <h3 className="font-extrabold text-[var(--ssc-navy)]">Other Offers You May Qualify For</h3>
      </div>
      <ul className="mt-3 space-y-2.5">
        {offers.map((o) => (
          <li key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`vd-offer-${o.id}`}>
            <span className="font-bold text-[var(--ssc-ink)] text-sm">{o.name}</span>
            {o.amount ? <span className="font-black text-[var(--ssc-navy)] tabular-nums text-sm">{fmtMoney(o.amount)}</span> : null}
            {o.apr_text && <span className="text-sm text-[var(--ssc-ink-2)]">{o.apr_text}</span>}
            {o.lease_text && <span className="text-sm text-[var(--ssc-ink-2)]">{o.lease_text}</span>}
            <span className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full border ${LABEL_STYLE[o.public_label] || LABEL_STYLE["Informational only"]}`}>
              {o.public_label}
            </span>
            {o.end_date && <span className="text-[11px] text-[var(--ssc-ink-2)]">ends {o.end_date}</span>}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] text-[var(--ssc-ink-2)]" data-testid="vd-offers-note">
        Some offers require qualification (military, first responder, loyalty, etc.) and are not
        included in the Final Price Before Taxes unless marked as included. Scott can confirm
        which offers you qualify for.
      </p>
    </div>
  );
}

function FeatureGroups({ options }) {
  const [showAll, setShowAll] = useState(false);
  const groups = useMemo(() => groupFeatures(options), [options]);
  if (!groups.length) return null;
  const PER_GROUP = 6;
  const hasMore = groups.some(([, items]) => items.length > PER_GROUP);

  return (
    <div data-testid="vd-features">
      <h3 className="font-extrabold text-[var(--ssc-navy)] text-xl mb-4">Features & Options</h3>
      <div className="grid md:grid-cols-2 gap-4">
        {groups.map(([name, items]) => (
          <div key={name} className="ssc-card" data-testid={`vd-feature-group-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="font-extrabold text-[var(--ssc-navy)] text-sm uppercase tracking-wide">{name}
              <span className="ml-2 text-[var(--ssc-ink-2)] font-semibold normal-case tracking-normal">({items.length})</span>
            </div>
            <ul className="mt-2 space-y-1 text-sm text-[var(--ssc-ink-2)]">
              {(showAll ? items : items.slice(0, PER_GROUP)).map((o, i) => (
                <li key={`${o}-${i}`} className="flex gap-2"><span className="text-[var(--ssc-blue)]">•</span><span>{o}</span></li>
              ))}
              {!showAll && items.length > PER_GROUP && (
                <li className="text-xs font-bold text-[var(--ssc-blue)]">+ {items.length - PER_GROUP} more</li>
              )}
            </ul>
          </div>
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll((s) => !s)}
          className="ssc-btn ssc-btn-outline mt-4"
          data-testid="vd-show-more-features"
        >
          {showAll ? <><ChevronUp size={16} /> Show Fewer Features</> : <><ChevronDown size={16} /> Show All {options.length} Features</>}
        </button>
      )}
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function VehicleDetail() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [error, setError] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [saved, setSaved] = useState(false);
  const [cmp, setCmp] = useState(false);
  const [cmpMsg, setCmpMsg] = useState("");
  const [quickMsg, setQuickMsg] = useState("");

  useEffect(() => {
    setError(false);
    setV(null);
    getVehicleBySlug(slug)
      .then((data) => {
        setV(data);
        setSaved(savedStore.has(slug));
        setCmp(compareStore.has(slug));
        return getSimilar(slug);
      })
      .then((d) => d && setSimilar(d.items || []))
      .catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    let lastPing = Date.now();
    let sid = "";
    try {
      sid = localStorage.getItem("ssc_chat_session") || "";
      if (!sid) {
        sid = "ssc_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("ssc_chat_session", sid);
      }
    } catch { /* ignore */ }
    const sendPing = () => {
      const secs = Math.round((Date.now() - lastPing) / 1000);
      if (secs <= 0) return;
      lastPing = Date.now();
      api.post("/track/vehicle-view", {
        slug, seconds: secs, session_id: sid, vehicle_title: v?.title || null,
      }).catch(() => {});
    };
    const interval = setInterval(sendPing, 30000);
    return () => { clearInterval(interval); sendPing(); };
  }, [slug, v?.title]);

  if (error) {
    return (
      <main id="main-content" className="ssc-container ssc-section text-center">
        <Seo title="Vehicle Not Available | Scott Sells Chevys" description="This vehicle may no longer be available." />
        <h1 className="text-3xl font-extrabold text-[var(--ssc-navy)]">This vehicle may no longer be available.</h1>
        <p className="mt-3 text-[var(--ssc-ink-2)]">It may have sold or changed. Let me help you find something similar.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/new-vehicles" className="ssc-btn ssc-btn-navy">Search New Inventory</Link>
          <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline">Search Used Inventory</Link>
          <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-primary">Find Me a Vehicle</Link>
          <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-red">Text Scott</a>
        </div>
      </main>
    );
  }

  if (!v) {
    return <main className="ssc-container ssc-section text-[var(--ssc-ink-2)]">Loading vehicle...</main>;
  }

  const title = `${v.year} ${v.make} ${v.model} ${v.trim || ""}`.trim();
  const photos = v.photos && v.photos.length ? v.photos : [];
  const isNew = v.status === "new";
  const detailPath = `/${v.status}-vehicles/${v.slug}`;
  const askPrefill = quickMsg ? `${quickMsg}\nStock #${v.stock_number} — ${title}` : "";
  const mpg = v.city_mpg && v.highway_mpg ? `${v.city_mpg} / ${v.highway_mpg} MPG` : null;

  const KEY_SPECS = [
    !isNew && v.mileage != null && { icon: Gauge, label: fmtMiles(v.mileage) },
    v.drivetrain && { icon: Cog, label: v.drivetrain },
    v.fuel_type && { icon: Fuel, label: v.fuel_type === "gas" ? "Gas" : v.fuel_type },
    mpg && { icon: BadgeCheck, label: mpg },
  ].filter(Boolean);

  return (
    <main id="main-content" className="bg-[var(--ssc-paper)]">
      <Seo
        title={`${title} | Scott Sells Chevys`}
        description={`${title} — Stock #${v.stock_number}. ${v.description ? v.description.slice(0, 140) : "Ask Scott about availability, payments, trade-in, and test drive options."}`}
        canonicalPath={detailPath}
        jsonLd={[
          breadcrumbs([
            { name: "Home", path: "/" },
            { name: isNew ? "New Vehicles" : "Used Vehicles", path: `/${v.status}-vehicles` },
            { name: title, path: detailPath },
          ]),
          vehicleSchema(v),
        ]}
      />

      <div className="ssc-container py-6 md:py-10 grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-8 items-start">
        <Gallery photos={photos} title={title} isNew={isNew} />

        <div className="min-w-0">
          <div className="text-[var(--ssc-ink-2)] text-xs font-semibold uppercase tracking-wide">
            Stock #{v.stock_number} <span className="mx-1 text-[var(--ssc-line)]">·</span> VIN {v.vin}
          </div>
          <h1 className="ssc-display text-2xl md:text-[34px] leading-tight font-extrabold text-[var(--ssc-navy)] mt-1" data-testid="vd-title">{title}</h1>

          {KEY_SPECS.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="vd-key-specs">
              {KEY_SPECS.map((s, i) => {
                const I = s.icon;
                return (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[var(--ssc-line)] text-xs font-bold text-[var(--ssc-navy)] capitalize">
                    <I size={13} className="text-[var(--ssc-blue)]" /> {s.label}
                  </span>
                );
              })}
            </div>
          )}

          <div className="mt-4">
            <PriceBreakdown v={v} />
            {isNew && <ByersPdfOffersSection v={v} />}
            {isNew && <OffersSection vin={v.vin} />}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <a href={`${siteConfig.scott.phoneSms}?&body=${encodeURIComponent(`Hi Scott, I'm interested in Stock ${v.stock_number} — ${title}.`)}`} className="ssc-btn ssc-btn-primary col-span-2" data-testid="vd-text-scott">
              <MessageSquareText size={16} /> Text Scott About This Vehicle
            </a>
            <a href="#ask" className="ssc-btn ssc-btn-navy" data-testid="vd-check-avail">Check Availability</a>
            <Link to={`/schedule-test-drive?vin=${v.vin}&stock=${v.stock_number}&title=${encodeURIComponent(title)}`} className="ssc-btn ssc-btn-navy" data-testid="vd-test-drive">
              <Calendar size={16} /> Schedule Test Drive
            </Link>
            <Link
              to={`/scotts-deal-desk?vin=${v.vin}&stock=${v.stock_number}&title=${encodeURIComponent(title)}&status=${v.status}&url=${encodeURIComponent(detailPath)}`}
              className="ssc-btn ssc-btn-outline"
              data-testid="vd-byod"
            >
              <Award size={16} /> Build My Own Deal
            </Link>
            <Link to={`/payment-help?title=${encodeURIComponent(title)}`} className="ssc-btn ssc-btn-outline" data-testid="vd-payments">
              <DollarSign size={16} /> Ask About Payments
            </Link>
            <Link to={`/value-my-trade?for=${encodeURIComponent(title)}`} className="ssc-btn ssc-btn-outline" data-testid="vd-trade">
              <Tag size={16} /> Value My Trade
            </Link>
            <a href={siteConfig.links.financeApply} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-outline" data-testid="vd-finance">
              Apply for Financing
            </a>
            <button
              type="button"
              onClick={() => { savedStore.toggle(v); setSaved(!saved); }}
              className={`ssc-btn ${saved ? "ssc-btn-red" : "ssc-btn-outline"}`}
              data-testid="vd-save"
            >
              <Heart size={16} /> {saved ? "Saved" : "Save Vehicle"}
            </button>
            <button
              type="button"
              onClick={() => {
                if (cmp) {
                  setCmpMsg("Already in your compare list — see it below.");
                  setTimeout(() => setCmpMsg(""), 4000);
                  return;
                }
                const res = compareStore.add(v);
                if (!res.ok) {
                  if (res.reason === "limit") {
                    setCmpMsg(COMPARE_LIMIT_MSG);
                  } else {
                    setCmpMsg("Already in your compare list — see it below.");
                  }
                  setTimeout(() => setCmpMsg(""), 5000);
                  return;
                }
                setCmp(true);
                setCmpMsg("Added to compare");
                setTimeout(() => setCmpMsg(""), 2500);
              }}
              className={`ssc-btn ${cmp ? "ssc-btn-navy" : "ssc-btn-outline"}`}
              data-testid="vd-compare"
            >
              <GitCompare size={16} /> {cmp ? "Added to Compare" : "Add to Compare"}
            </button>
            {cmp && (
              <Link
                to="/compare"
                className="ssc-btn ssc-btn-outline !text-xs !py-2"
                data-testid="vd-view-compare"
              >
                View Compare →
              </Link>
            )}
            {cmpMsg && (
              <div
                className="col-span-2 text-xs font-bold text-[var(--ssc-blue)] bg-blue-50 border border-blue-200 rounded-lg px-3 py-2"
                data-testid="vd-compare-toast"
              >
                {cmpMsg}
              </div>
            )}
            <a href={siteConfig.links.accessories} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-outline col-span-2" data-testid="vd-accessories">
              <ShoppingBag size={16} /> Shop Chevrolet Accessories
            </a>
          </div>
        </div>
      </div>

      <section className="ssc-container pb-2">
        <div className="rounded-2xl border border-[var(--ssc-line)] bg-white shadow-sm p-5 md:p-6" data-testid="vd-specs">
          <h3 className="font-extrabold text-[var(--ssc-navy)] text-xl mb-4">Vehicle Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
            <Detail k="Trim" val={v.trim} />
            <Detail k="Body" val={v.body_style} />
            <Detail k="Engine" val={v.engine} />
            <Detail k="Transmission" val={v.transmission} />
            <Detail k="Drivetrain" val={v.drivetrain} />
            <Detail k="Fuel" val={v.fuel_type === "gas" ? "Gas" : v.fuel_type} />
            {mpg && <Detail k="City / Hwy MPG" val={mpg} />}
            {v.ev_range != null && <Detail k="EV Range" val={`${v.ev_range} mi`} />}
            <Detail k="Exterior" val={v.exterior_color} />
            <Detail k="Interior" val={v.interior_color} />
            {v.interior_material && <Detail k="Interior Material" val={v.interior_material} />}
            {!isNew && v.mileage != null && <Detail k="Mileage" val={fmtMiles(v.mileage)} />}
          </div>
        </div>
      </section>

      <section className="ssc-container py-6 space-y-6">
        {v.description && (
          <div className="rounded-2xl border border-[var(--ssc-line)] bg-white shadow-sm p-5 md:p-6" data-testid="vd-description">
            <h3 className="font-extrabold text-[var(--ssc-navy)] text-xl mb-2">About This Vehicle</h3>
            <p className="text-[var(--ssc-ink-2)] leading-relaxed">{v.description}</p>
          </div>
        )}
        {v.options && v.options.length > 0 && <FeatureGroups options={v.options} />}
      </section>

      <section id="ask" className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container max-w-3xl">
          <SectionHeader title="Ask Scott About This Vehicle" subtitle="Pick a quick question or write your own. I'll get back to you with real next steps." />
          <div className="flex flex-wrap gap-2 mb-4">
            {QUICK.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setQuickMsg(q)}
                className={`ssc-chip ${quickMsg === q ? "ssc-chip-active" : ""}`}
                data-testid={`quick-q-${i}`}
              >
                {q}
              </button>
            ))}
          </div>
          <LeadForm
            leadType="vehicle_inquiry"
            leadSource="Vehicle Detail Page"
            testIdPrefix="vd-lead"
            key={quickMsg}
            fields={[
              { name: "name", label: "Name", required: true, autoComplete: "name" },
              { name: "phone", label: "Phone", inputMode: "tel", autoComplete: "tel" },
              { name: "email", label: "Email", type: "email", autoComplete: "email" },
              { name: "preferred_contact", label: "Preferred Contact", type: "select", options: ["Text", "Call", "Email"] },
              { name: "message", label: "Question", type: "textarea", full: true, placeholder: askPrefill || "Ask anything about this vehicle..." },
            ]}
            hidden={{
              vehicle_title: title,
              vin: v.vin,
              stock_number: v.stock_number,
              new_or_used: v.status,
              cta_clicked: quickMsg || "Ask Scott (free form)",
            }}
            submitLabel="Send to Scott"
          />
          <div className="mt-4">
            <Disclaimer text={disclaimers.inventory} />
          </div>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container">
          <SectionHeader title="Similar Vehicles" />
          {similar && similar.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {similar.map((sv) => <VehicleCard key={sv.id || sv.slug} vehicle={sv} />)}
            </div>
          ) : (
            <div className="rounded-2xl border border-[var(--ssc-line)] bg-white p-6 text-center" data-testid="vd-similar-fallback">
              <p className="text-[var(--ssc-ink-2)] mb-4">
                Want something similar? Text Scott and I'll help find the closest match.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary">
                  <MessageSquareText size={16} /> Text Scott
                </a>
                <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-navy">Find Me a Vehicle</Link>
                <Link to={`/${v.status}-vehicles`} className="ssc-btn ssc-btn-outline">
                  Browse All {isNew ? "New" : "Used"}
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

const Detail = ({ k, val }) => val ? (
  <div className="rounded-xl bg-[var(--ssc-paper)] border border-[var(--ssc-line)]/70 px-3 py-2.5">
    <div className="text-[var(--ssc-ink-2)] text-[10px] uppercase tracking-wider font-bold">{k}</div>
    <div className="text-[var(--ssc-navy)] font-semibold capitalize mt-0.5">{String(val)}</div>
  </div>
) : null;